import { storeToRefs } from 'pinia'
import {
  computed,
  onScopeDispose,
  ref,
  shallowRef,
  toValue,
  watch,
  type ComputedRef,
  type MaybeRefOrGetter,
  type Ref,
  type ShallowRef,
} from 'vue'

import { reportPreferencesApi } from '../api/ReportPreferencesApi'
import type {
  ReportFilter,
  SavedReportFilterInput,
  SavedReportFilterOption,
} from '../models/filter.types'
import {
  addSavedReportFilter,
  parseSavedReportFilters,
  removeSavedReportFilter,
  serializeSavedReportFilters,
  updateSavedReportFilter,
} from '../service/savedReportFilters'
import { useSavedReportFiltersStore } from '../stores/savedReportFiltersStore'

import { useAccount, useWorkspace } from '@/features/account'

export type SavedReportFiltersState = 'idle' | 'loading' | 'ready' | 'error'

export interface UseSavedReportFilters {
  savedFilters: Ref<SavedReportFilterOption[]>
  state: Ref<SavedReportFiltersState>
  error: ShallowRef<unknown | null>
  busy: ComputedRef<boolean>
  /** `force` bypasses the scope cache — a scope change already does this on its own. */
  refresh: (force?: boolean) => Promise<void>
  apply: (id: string) => ReportFilter | null
  create: (filter: SavedReportFilterInput) => Promise<void>
  update: (id: string, filter: SavedReportFilterInput) => Promise<void>
  remove: (id: string) => Promise<void>
  cancel: () => void
}

/** Server-backed, account+workspace-scoped saved filters with serialized writes and
 *  stale-request guards. Backed by `savedReportFiltersStore`, a persisted per-scope cache — a
 *  component remount or hard reload with the same scope reuses the cached records instead of
 *  refetching. */
export function useSavedReportFilters(
  enabled: MaybeRefOrGetter<boolean> = true,
): UseSavedReportFilters {
  const account = useAccount()
  const workspace = useWorkspace()
  const cacheStore = useSavedReportFiltersStore()
  const { filters: savedFilters } = storeToRefs(cacheStore)
  const state = ref<SavedReportFiltersState>('idle')
  const error = shallowRef<unknown | null>(null)
  let controller: AbortController | null = null
  let activeRequest = 0
  let mutationTail: Promise<void> = Promise.resolve()

  const scope = computed(() => {
    if (!toValue(enabled)) return null
    const accountId = account.accountId.value
    const workspaceId = workspace.workspaceId.value
    return accountId === undefined || workspaceId === undefined
      ? null
      : { accountId, workspaceId, key: `${accountId}:${String(workspaceId)}` }
  })

  const cancel = (): void => {
    activeRequest += 1
    controller?.abort()
    controller = null
  }

  const refresh = async (force = false): Promise<void> => {
    const target = scope.value
    if (!target) {
      cancel()
      state.value = 'idle'
      error.value = null
      return
    }
    if (!force && cacheStore.ownsFreshScope(target.key)) {
      state.value = 'ready'
      error.value = null
      return
    }
    controller?.abort()
    controller = new AbortController()
    const requestId = ++activeRequest
    state.value = 'loading'
    error.value = null
    try {
      const value = await reportPreferencesApi.fetchSavedFilters(
        target.accountId,
        target.workspaceId,
        controller.signal,
      )
      if (requestId !== activeRequest || controller.signal.aborted) return
      cacheStore.setFilters(target.key, parseSavedReportFilters(value))
      state.value = 'ready'
    } catch (cause) {
      if (requestId !== activeRequest || controller.signal.aborted) return
      error.value = cause
      state.value = 'error'
    } finally {
      if (requestId === activeRequest) controller = null
    }
  }

  const mutate = (
    transform: (current: readonly SavedReportFilterOption[]) => SavedReportFilterOption[],
  ) => {
    const requestedScope = scope.value
    if (!requestedScope) return Promise.resolve()
    const operation = mutationTail.then(async () => {
      if (scope.value?.key !== requestedScope.key) return
      if (!cacheStore.ownsFreshScope(requestedScope.key)) await refresh()
      if (
        scope.value?.key !== requestedScope.key ||
        !cacheStore.ownsFreshScope(requestedScope.key)
      ) {
        if (error.value) throw error.value
        return
      }
      const next = transform(savedFilters.value)
      controller?.abort()
      const writeController = new AbortController()
      controller = writeController
      const requestId = ++activeRequest
      state.value = 'loading'
      error.value = null
      try {
        await reportPreferencesApi.replaceSavedFilters(
          requestedScope.accountId,
          requestedScope.workspaceId,
          serializeSavedReportFilters(next),
          writeController.signal,
        )
        if (
          requestId === activeRequest &&
          !writeController.signal.aborted &&
          scope.value?.key === requestedScope.key
        ) {
          cacheStore.replaceFilters(requestedScope.key, next)
          state.value = 'ready'
        }
      } catch (cause) {
        if (requestId !== activeRequest || writeController.signal.aborted) return
        error.value = cause
        state.value = 'error'
        throw cause
      } finally {
        if (requestId === activeRequest) controller = null
      }
    })
    mutationTail = operation.catch(() => {})
    return operation
  }

  watch(scope, () => void refresh(), { immediate: true })
  onScopeDispose(cancel)
  return {
    savedFilters,
    state,
    error,
    busy: computed(() => state.value === 'loading'),
    refresh,
    apply: (id) => {
      const saved = savedFilters.value.find((item) => item.id === id)
      return saved
        ? {
            ...saved.filter,
            value: Array.isArray(saved.filter.value) ? [...saved.filter.value] : saved.filter.value,
          }
        : null
    },
    create: (filter) => mutate((current) => addSavedReportFilter(current, filter)),
    update: (id, filter) => mutate((current) => updateSavedReportFilter(current, id, filter)),
    remove: (id) => mutate((current) => removeSavedReportFilter(current, id)),
    cancel,
  }
}
